import React, { useEffect, useState } from 'react';
import { Tabs, List, Avatar, Breadcrumb, Spin, Typography, Empty, DatePicker } from 'antd';
import { HomeOutlined, InboxOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import AppreciateSVG from '../../assets/images/Appreciate.svg';
import RegionalAwards from '../../assets/images/RewardsImages/RegionalAwards.svg';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';

const { TabPane } = Tabs;
const { Text } = Typography;

interface LeaderBoardUser {
  userId: string;
  userName: string;
  userImage: string;
  totalPoints?: number;
  winCount?: number;
  department: string;
  country: string;
}

const LeaderBoardComponent: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('appreciations');
  const [appreciationData, setAppreciationData] = useState<LeaderBoardUser[]>([]);
  const [pointsData, setPointsData] = useState<LeaderBoardUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedYear, setSelectedYear] = useState<Dayjs>(dayjs());
  const { instance, accounts } = useMsal();
  const { t } = useTranslation();

  const fetchAppreciationData = async (year: number = new Date().getFullYear()) => {
    console.log('Fetching appreciation data for year:', year);
    setLoading(true);
    try {
      const token = await fetchToken();
      const url = `${process.env.REACT_APP_API_URL}/api/LeaderBoard/getUsersWithHighestAppreciations/${year}`;
      console.log('API URL:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await response.json();
      console.log('Appreciation data received:', data);
      setAppreciationData(data);
    } catch (error) {
      console.error('Error fetching appreciation data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    return res.idToken;
  };

  const fetchPointsData = async (year: number = new Date().getFullYear()) => {
    console.log('Fetching points data for year:', year);
    setLoading(true);
    try {
      const token = await fetchToken();
      const url = `${process.env.REACT_APP_API_URL}/api/LeaderBoard/getUsersWithHighestPoints/${year}`;
      console.log('API URL:', url);
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      ;
      const data = await response.json();
      console.log('Points data received:', data);
      setPointsData(data);
    } catch (error) {
      console.error('Error fetching points data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const year = selectedYear.year();
    console.log('Year changed to:', year, 'Active tab:', activeTab);
    if (activeTab === 'appreciations') {
      fetchAppreciationData(year);
    } else if (activeTab === 'points') {
      fetchPointsData(year);
    }
  }, [activeTab, selectedYear]);

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  const renderList = (data: LeaderBoardUser[]) => (
    <div style={{ 
      display: 'grid', 
      gridTemplateColumns: 'repeat(5, 1fr)', 
      gap: '20px',
      width: '100%'
    }}>
      {data.map((item, index) => (
        <div
          key={item.userId}
          style={{
            width: '100%',
            minWidth: 0,
            height: '264px',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            padding: '20px',
            border: '1px solid #e0e0e0',
            textAlign: 'center',
          }}
        >
          {/* Avatar */}
          {item.userImage ? (
            <Avatar
              src={`data:image/png;base64,${item.userImage}`}
              size={80}
              style={{ marginBottom: '10px' }}
            />
          ) : (
            <Avatar
              style={{
                backgroundColor: getRandomColor(),
                color: '#fff',
                fontSize: '32px',
                marginBottom: '10px',
              }}
              size={80}
            >
              {item.userName.charAt(0).toUpperCase()}
            </Avatar>
          )}

          {/* Name with Tooltip */}
          <div >
            <Text
              strong
              style={{
                fontSize: '18px',
                color: '#333',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
              }}
              title={item.userName} // Tooltip for full name
            >
              <span>{item.userName}</span><br />
              <span style={{
                fontSize: '12px',
                color: '#9e9e9e' ,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
                position:'relative',
                bottom:'4px'
              }}>{item.country}</span>
            </Text>
          </div>

          {/* Points and Awards */}
          {activeTab === 'appreciations' ? (
            // Appreciations Tab: Only Heart Icon and Wins
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#e91e63' }}>
                <div className="w-20 h-20 md:w-24 md:h-24 flex items-center justify-center"  style={{ height: '50px' }}>
                  <img src={AppreciateSVG} alt="Wall of Honour" className="w-8 h-8 object-contain" style={{ height: '50px' }} />
                </div>              </div>
              <div style={{ fontSize: 'px', color: '#9e9e9e' }}>Appreciations</div>
              <div style={{ color: '#e91e63', fontWeight: 'bold', fontSize: '18px' }}>
                {item.winCount !== undefined ? item.winCount : '--'}
              </div>
            </div>
          ) : (
            // Rewards Tab: Points and Awards
            <div
              style={{
                marginTop: '10px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                alignItems: 'center',
                gap: '2px', 
              }}
            >
              {/* Points */}
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#9e9e9e' }}>Points Accumulated</div>
                <div style={{ color: '#4caf50', fontWeight: 'bold', fontSize: '17px' }}>
                  {item.totalPoints !== undefined ? Math.round(item.totalPoints) : '--'}
                </div>
              </div>

              {/* Awards Icon */}
              <div style={{ textAlign: 'center', position: 'relative' }}>
                <div
                  style={{
                    fontSize: '24px',
                    color: '#2196f3',
                    height: '70px',
                    width: '70px',
                    backgroundImage: `url(${RegionalAwards})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    opacity: 0.3,
                    margin: '0 auto',
                  }}
                ></div>
              </div>

              {/* Awards */}
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '11px', color: '#9e9e9e' }}>{selectedYear.year()} Awards</div>
                <div style={{ color: '#2196f3', fontWeight: 'bold', fontSize: '17px' }}>
                  {item.winCount !== undefined ? item.winCount : '--'}
                </div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
  const getRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  return (
    <div style={styles.container}>
      {/* Breadcrumb Section */}
      {/* <div style={styles.breadcrumbContainer}>
       
      </div> */}
      <div style={{ padding: '10px', marginTop: '10px', fontSize: '24px', backgroundColor: 'white', height: '84vh' }}>      <Breadcrumb style={styles.breadcrumb}>
        <Breadcrumb.Item>
          <Link to="/home">
            <HomeOutlined style={styles.homeIcon} />
            {t('home')}
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>{t('leaderboard')}</Breadcrumb.Item>
      </Breadcrumb>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '0px 12px', 
          margin: '0px 10px'
        }}>
          <h4 style={{ fontSize: '20px', backgroundColor: "white", margin: 0 }}>{t('leaderboard_welcome')} 🏆</h4>
          <DatePicker 
            picker="year" 
            value={selectedYear}
            onChange={(date) => {
              if (date) {
                console.log('DatePicker onChange - Selected year:', date.year());
                setSelectedYear(date);
              }
            }}
            style={{ width: '120px' }}
            allowClear={false}
            disabledDate={(current) => current && current.year() < 2025}
          />
        </div>
        <p style={{ padding: '2px 12px', margin: '10px', fontSize: '14px' }} >
          {t('leaderboard_description')}  
        </p>
        <Tabs
          activeKey={activeTab}
          onChange={handleTabChange}
          type="card"
          style={styles.tabs}
        >
          <TabPane tab={t('appreciations')} key="appreciations">
            {loading ? (
              <div style={styles.spinnerContainer as React.CSSProperties}>
                <Spin size="large" />
              </div>
            ) : appreciationData.length === 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 20px'
              }}>
                <InboxOutlined style={{ fontSize: '80px', color: '#d9d9d9', marginBottom: '16px' }} />
                <Text style={{ fontSize: '16px', color: '#8c8c8c' }}>No appreciation data available</Text>
              </div>
            ) : (
              renderList(appreciationData)
            )}
          </TabPane>
          <TabPane tab={t('awards')} key="points">
            {loading ? (
              <div style={styles.spinnerContainer as React.CSSProperties}>
                <Spin size="large" />
              </div>
            ) : pointsData.length === 0 ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 20px'
              }}>
                <InboxOutlined style={{ fontSize: '80px', color: '#d9d9d9', marginBottom: '16px' }} />
                <Text style={{ fontSize: '16px', color: '#8c8c8c' }}>No awards data available</Text>
              </div>
            ) : (
              renderList(pointsData)
            )}
          </TabPane>
        </Tabs>
      </div>
    </div>

  );
};

const styles = {
  container: {
    padding: '10px',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
  },
  breadcrumbContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    padding: '15px 20px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  breadcrumb: {
    backgroundColor: 'transparent',
    padding: '14px',
    margin: 0,
  },
  homeIcon: {
    marginRight: '5px',
    color: '#1890ff',
  },
  tabs: {
    marginTop: '10px',
    backgroundColor: 'white',
    padding: '15px 20px',
    borderRadius: '8px',
  },
  spinnerContainer: {
    textAlign: 'center',
    padding: '20px',
  },
  listItem: {
    padding: '15px',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    marginBottom: '10px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  '@media (max-width: 768px)': {
    container: {
      padding: '5px',
    },
    card: {
      width: '100%', // Full width on smaller screens
      height: 'auto',
    },
    tabs: {
      padding: '10px',
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  rank: {
    fontSize: '18px',
    color: '#673ab7',
    marginRight: '10px',
  },
  userName: {
    fontSize: '16px',
    color: '#333',
  },
  department: {
    marginLeft: '10px',
    color: '#ff9800',
  },
  descriptionContainer: {
    marginTop: '5px',
  },
  points: {
    color: '#4caf50',
    fontWeight: 'bold',
  },
  appreciations: {
    color: '#2196f3',
    fontWeight: 'bold',
  },
  country: {
    marginLeft: '10px',
    color: '#9e9e9e',
  },
};

export default LeaderBoardComponent;